import type { AgentSopSummary } from '@erp/rpc';
import type { BadgeProps } from 'src/components/ui/badge';

// Shared procedure display helpers, so the main Procedures list and the agent's Procedures tab
// render the same label + site the same way (they show the same data through the same DataTable).

/** A SOP authored without a title still has its kebab `name`; show that instead of blank. */
export const sopLabel = (sop: AgentSopSummary): string =>
  sop.title.trim() || sop.name;

/**
 * The procedure's unique kebab `name`, shown as secondary text in pickers because titles are not
 * unique (an imported copy keeps its title). Null when the label already is the name.
 */
export function sopKey(
  sop: Pick<AgentSopSummary, 'title' | 'name'>,
): string | null {
  const title = sop.title.trim();
  return title && title !== sop.name ? sop.name : null;
}

/** The site a procedure runs against — its start URL's host (sans www), or the stored siteKey.
 * Far more scannable in a list than a Status column where every row reads "active". */
export function siteOf(sop: AgentSopSummary): string | null {
  if (sop.startUrl) {
    try {
      return new URL(sop.startUrl).hostname.replace(/^www\./, '');
    } catch {
      // Malformed URL — fall back to the siteKey below.
    }
  }
  return sop.siteKey || null;
}

/**
 * Case-insensitive filter over title and kebab name, shared by both procedure lists.
 *
 * The list grows a long tail of test/"Untitled" procedures, so a quick filter beats scrolling —
 * and matching the `name` too means a kebab slug pasted from a URL finds its row.
 */
export function filterSopsByQuery<T extends AgentSopSummary>(
  sops: T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return sops;
  return sops.filter(
    (sop) =>
      sop.title.toLowerCase().includes(q) || sop.name.toLowerCase().includes(q),
  );
}

/** Badge colour per stored status string — tolerant of legacy values like "published". */
export const SOP_STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  draft: 'secondary',
  active: 'success',
  published: 'success',
  archived: 'outline',
};
